import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserRole, WorkerProfile, JobRequest, UserProfile, ServiceCategory, PlatformFeeConfig, Language } from '../types';
import { supabase } from '../services/supabaseClient';
import {
    saveWorkerProfile,
    listenToCategories,
    listenToPlatformConfig,
    listenToMyJobs,
    listenToBroadcastJobs,
    listenToUserProfile,
    listenToWalletUpdates,
    getUserProfile,
    getAllWorkers,
    getWorkerProfile,
    saveUserProfile,
    deleteUser
} from '../services/firestoreService';
import { DEFAULT_SERVICE_CATEGORIES, DEFAULT_FEE_CONFIG } from '../constants';
import { notificationService } from '../services/notificationService';

interface SessionContextType {
    showSplash: boolean;
    isSessionLoading: boolean;
    language: Language;
    setLanguage: (lang: Language) => void;
    hasSelectedLang: boolean;
    setHasSelectedLang: (selected: boolean) => void;
    role: UserRole;
    setRole: (role: UserRole) => void;
    isLoggedIn: boolean;
    setIsLoggedIn: (loggedIn: boolean) => void;
    isOnboarding: boolean;
    setIsOnboarding: (onboarding: boolean) => void;
    showTour: boolean;
    setShowTour: (show: boolean) => void;
    serviceCategories: ServiceCategory[];
    feeConfig: PlatformFeeConfig;
    userProfile: UserProfile;
    setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
    activeWorkerProfile: WorkerProfile | null;
    setActiveWorkerProfile: React.Dispatch<React.SetStateAction<WorkerProfile | null>>;
    myJobs: JobRequest[];
    broadcastJobs: JobRequest[];
    incomingJob: JobRequest | null;
    setIncomingJob: (job: JobRequest | null) => void;
    performLogout: (shouldReload?: boolean) => void;
    handleAuthSuccess: (identifier: string, forcedRole: UserRole, name?: string) => Promise<boolean>;
    handleOnboardingComplete: (profile: UserProfile, workerDetails?: Partial<WorkerProfile>) => Promise<void>;
    handleProfileUpdate: (profile: UserProfile) => void;
    setFeeConfig: React.Dispatch<React.SetStateAction<PlatformFeeConfig>>;
    chattingJob: JobRequest | null;
    setChattingJob: (job: JobRequest | null) => void;
    reloadSession: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [showSplash, setShowSplash] = useState(true);
    const [isSessionLoading, setIsSessionLoading] = useState(true);

    const [language, setLanguage] = useState<Language>(() => (localStorage.getItem('sl_lang') as Language) || 'en');
    const [hasSelectedLang, setHasSelectedLang] = useState(() => !!localStorage.getItem('sl_lang'));

    const [role, setRole] = useState<UserRole>(UserRole.NONE);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isOnboarding, setIsOnboarding] = useState(false);
    const [showTour, setShowTour] = useState(false);

    const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>(DEFAULT_SERVICE_CATEGORIES);
    const [feeConfig, setFeeConfig] = useState<PlatformFeeConfig>(DEFAULT_FEE_CONFIG);

    const [userProfile, setUserProfile] = useState<UserProfile>({
        firstName: '', lastName: '', email: '', phone: '', gender: '', dob: '', avatarUrl: 'https://cdn-icons-png.flaticon.com/512/149/149071.png',
        walletBalance: 0, language: 'en'
    });

    const [activeWorkerProfile, setActiveWorkerProfile] = useState<WorkerProfile | null>(null);
    const [myJobs, setMyJobs] = useState<JobRequest[]>([]);
    const [broadcastJobs, setBroadcastJobs] = useState<JobRequest[]>([]);
    const [incomingJob, setIncomingJob] = useState<JobRequest | null>(null);
    const alertedJobIdsRef = React.useRef<Set<string>>(new Set());
    const [chattingJob, setChattingJobState] = useState<JobRequest | null>(() => {
        const saved = localStorage.getItem('hh_chatting_job');
        return saved ? JSON.parse(saved) : null;
    });

    const setChattingJob = (job: JobRequest | null) => {
        if (job) localStorage.setItem('hh_chatting_job', JSON.stringify(job));
        else localStorage.removeItem('hh_chatting_job');
        setChattingJobState(job);
    };

    const lastJobStatuses = React.useRef<Record<string, string>>({});
    const lastBroadcastIds = React.useRef<Set<string>>(new Set());
    const myJobsRef = React.useRef<JobRequest[]>([]);

    const performLogout = async (shouldReload = true) => {
        console.log('🚪 Performing full logout & cache clear...');

        // 1. Sign out from Supabase (global scope invalidates all sessions)
        try {
            await supabase.auth.signOut({ scope: 'global' });
        } catch (e) {
            console.error("Supabase signOut error", e);
            // Try local fallback if global fails
            try { await supabase.auth.signOut({ scope: 'local' }); } catch { }
        }

        // 2. Remove all Supabase realtime channels
        try {
            supabase.removeAllChannels();
        } catch (e) {
            console.error("Remove channels error", e);
        }

        // 3. Reset all React state
        setIsLoggedIn(false);
        setRole(UserRole.NONE);
        setUserProfile({
            firstName: '', lastName: '', email: '', phone: '', gender: '', dob: '',
            avatarUrl: 'https://cdn-icons-png.flaticon.com/512/149/149071.png',
            walletBalance: 0, language: 'en'
        });
        setLanguage('en');
        setHasSelectedLang(false);
        setActiveWorkerProfile(null);
        setMyJobs([]);
        setBroadcastJobs([]);
        setIncomingJob(null);
        setChattingJob(null);

        // 4. Clear ALL localStorage (complete wipe)
        try {
            localStorage.clear();
            console.log('✅ localStorage cleared');
        } catch (e) {
            console.error("localStorage clear error", e);
        }

        // 5. Clear ALL sessionStorage
        try {
            sessionStorage.clear();
            console.log('✅ sessionStorage cleared');
        } catch (e) {
            console.error("sessionStorage clear error", e);
        }

        // 6. Clear Cache API (all browser caches)
        try {
            if ('caches' in window) {
                const cacheKeys = await caches.keys();
                for (const key of cacheKeys) {
                    await caches.delete(key);
                }
                console.log('✅ Cache API cleared:', cacheKeys.length, 'caches');
            }
        } catch (e) {
            console.error("Cache API clear error", e);
        }

        // 7. Unregister Service Workers
        try {
            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                for (const reg of registrations) {
                    await reg.unregister();
                }
                console.log('✅ Service workers unregistered:', registrations.length);
            }
        } catch (e) {
            console.error("Service Worker unregister error", e);
        }

        // 8. Clear IndexedDB (Supabase stores auth tokens here)
        try {
            const databases = await window.indexedDB.databases?.() || [];
            for (const db of databases) {
                if (db.name) {
                    window.indexedDB.deleteDatabase(db.name);
                }
            }
            console.log('✅ IndexedDB cleared');
        } catch (e) {
            // indexedDB.databases() not supported in all browsers
            // Try clearing known Supabase databases
            try {
                window.indexedDB.deleteDatabase('supabase-auth-token');
            } catch { }
        }

        console.log('🧹 Full cache clear complete. Redirecting...');

        // 9. Force hard reload to root (ensures no stale state remains)
        if (shouldReload) {
            // Use location.replace to prevent back-button returning to cached page
            window.location.replace('/');
        }
    };

    const reloadSession = async () => {
        setIsSessionLoading(true);
        try {
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
            if (!session) {
                if (localStorage.getItem('hh_role')) await performLogout(false);
                else {
                    setIsLoggedIn(false);
                    setRole(UserRole.NONE);
                }
                return; // Early return, finally block will handle loading state
            }

            // Check for pending role (OAuth Web Flow Fix)
            const pendingRole = localStorage.getItem('hh_pending_role');
            let currentProfile = await getUserProfile(session.user.id);

            // SECURITY: If profile missing but we have pending role (New Google User), create it immediately
            if (!currentProfile && pendingRole) {
                console.log("🛠️ Creating fresh profile for new Google User...");

                // Get name from metadata or split email or default
                let fullName = 'User';
                if (session.user.user_metadata?.full_name) fullName = session.user.user_metadata.full_name;
                else if (session.user.user_metadata?.name) fullName = session.user.user_metadata.name;

                const skeleton: UserProfile = {
                    id: session.user.id,
                    firstName: fullName.split(' ')[0],
                    lastName: fullName.split(' ').slice(1).join(' ') || '',
                    email: session.user.email || '',
                    phone: session.user.phone || '+91 ',
                    role: pendingRole as UserRole, // Enforce the selected role
                    gender: 'Male', dob: '', avatarUrl: session.user.user_metadata?.avatar_url || 'https://cdn-icons-png.flaticon.com/512/149/149071.png',
                    walletBalance: 0, language: 'en', status: 'active'
                };

                try {
                    await saveUserProfile(skeleton);
                    currentProfile = skeleton;

                    // ✅ FIX: Auto-set preferences for Google OAuth users (skip landing/language screens)
                    localStorage.setItem('sl_lang', 'en');
                    localStorage.setItem('hh_seen_landing', 'true');
                    localStorage.removeItem('hh_pending_role');

                    console.log("✅ Google OAuth profile created successfully!");
                } catch (err) {
                    console.error("Failed to create skeleton profile", err);
                }
            }

            if (pendingRole && currentProfile && currentProfile.role !== pendingRole) {
                console.log(`⚠️ Role Mismatch (Web)! Fixing...`);
                await saveUserProfile({ ...currentProfile, role: pendingRole as UserRole } as any);
                currentProfile.role = pendingRole as UserRole;
                localStorage.removeItem('hh_pending_role');
            }

            // Ensure worker profile exists if needed
            if (currentProfile?.role === UserRole.WORKER) {
                const existingWorker = await getWorkerProfile(session.user.id);
                if (!existingWorker) {
                    await saveWorkerProfile({
                        id: session.user.id,
                        phone: currentProfile.phone || '',
                        name: `${currentProfile.firstName} ${currentProfile.lastName}`.trim(),
                        skill: 'General',
                        status: 'active',
                        verificationStatus: 'pending',
                        avatarUrl: currentProfile.avatarUrl,
                        hourlyRate: 300,
                        isOnline: false,
                        lifetimeJobsCompleted: 0,
                        totalEarned: 0,
                        reviewCount: 0,
                        rating: 5.0,
                        tags: [],
                        distanceKm: 0,
                        bio: 'Available for work',
                        coordinates: { x: 0, y: 0 }
                    });
                }
            }

            if (currentProfile) {
                if (currentProfile.status === 'suspended' || currentProfile.status === 'deleted') {
                    await performLogout(false);
                    return;
                }
                setUserProfile(currentProfile);
                setRole(currentProfile.role === UserRole.WORKER ? UserRole.WORKER : UserRole.CUSTOMER);
                setIsLoggedIn(true);
                // Important: Ensure local storage matches the established session
                localStorage.setItem('hh_role', currentProfile.role || UserRole.CUSTOMER);

                // Force onboarding ONLY if phone is missing AND this is a NEW OAuth user
                const isNewOAuthUser = localStorage.getItem('hh_pending_role') !== null;

                if (!currentProfile.phone || currentProfile.phone.trim() === '') {
                    if (isNewOAuthUser) {
                        // New Google OAuth user without phone - needs onboarding
                        console.log("⚠️ New Google OAuth user missing phone number. Forcing Onboarding.");
                        setIsOnboarding(true);
                        localStorage.setItem('hh_needs_onboarding', 'true');
                    } else {
                        // Existing user who somehow lost phone - set onboarding but don't redirect
                        console.log("⚠️ Existing user missing phone number. Setting onboarding mode.");
                        setIsOnboarding(true);
                        localStorage.removeItem('hh_needs_onboarding'); // Don't force redirect
                    }
                } else {
                    // Phone exists - clear onboarding flags
                    setIsOnboarding(false);
                    localStorage.removeItem('hh_needs_onboarding');
                }

                if (currentProfile.role === UserRole.WORKER) {
                    const myWorker = await getWorkerProfile(session.user.id);
                    // Check if worker profile is truly complete (not just existing)
                    const isWorkerComplete = myWorker &&
                        myWorker.skill !== 'General' &&
                        myWorker.bio &&
                        myWorker.bio !== 'Available for work';

                    if (myWorker && isWorkerComplete) {
                        setActiveWorkerProfile(myWorker);
                        // Worker profile exists and is complete
                        localStorage.removeItem('hh_needs_onboarding');
                        setIsOnboarding(false);
                    } else {
                        // Worker profile missing OR incomplete -> Force Onboarding
                        console.log("⚠️ Worker profile incomplete. Forcing Onboarding.");
                        setIsOnboarding(true);
                        if (myWorker) setActiveWorkerProfile(myWorker); // Set partial profile so we can edit it
                    }
                }
            } else {
                // Profile missing and no pending role? Force logout to restart flow
                console.warn("⚠️ Auth User exists but Profile missing. Forcing logout.");
                await performLogout(false);
            }

        } catch (e) {
            console.error("Session restore error", e);
        } finally {
            setIsSessionLoading(false);
            setShowSplash(false);
        }
    };

    useEffect(() => {
        const safetyTimeout = setTimeout(() => {
            if (showSplash || isSessionLoading) {
                setIsSessionLoading(false);
                setShowSplash(false);
            }
        }, 8000);

        reloadSession();

        return () => clearTimeout(safetyTimeout);
    }, []);

    useEffect(() => {
        localStorage.setItem('sl_lang', language);
    }, [language]);

    useEffect(() => {
        if (isLoggedIn && userProfile.id && !isOnboarding) {
            return listenToUserProfile(userProfile.id, (updated) => {
                if (updated) {
                    setUserProfile(updated);
                    if (updated.status === 'suspended') {
                        alert("Account suspended.");
                        performLogout();
                    }
                }
            });
        }
    }, [isLoggedIn, userProfile.id, isOnboarding]);

    useEffect(() => {
        let lastLat: number | null = null;
        let lastLng: number | null = null;
        let intervalId: any;

        const syncLocation = async () => {
            if ('geolocation' in navigator) {
                navigator.geolocation.getCurrentPosition(async (pos) => {
                    const { latitude, longitude } = pos.coords;
                    if (!lastLat || Math.abs(latitude - lastLat) > 0.0005 || Math.abs(longitude - lastLng!) > 0.0005) {
                        lastLat = latitude; lastLng = longitude;
                        if (isLoggedIn && userProfile.id) {
                            const table = role === UserRole.WORKER ? 'workers' : 'profiles';
                            await supabase.from(table).update({
                                current_lat: latitude, current_lng: longitude,
                                last_location_update: new Date().toISOString()
                            }).eq('id', userProfile.id);
                        }
                    }
                }, () => { }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 30000 });
            }
        };

        if (isLoggedIn) {
            syncLocation();
            intervalId = setInterval(syncLocation, 30000);
        }
        return () => clearInterval(intervalId);
    }, [isLoggedIn, userProfile.id, role]);

    useEffect(() => {
        const handleConfigUpdate = (config: PlatformFeeConfig) => {
            console.log('📡 Platform Config Updated from Listener:', {
                landingBackgroundType: config.landingBackgroundType,
                landingVideoUrl: config.landingVideoUrl,
                landingImageUrl: config.landingImageUrl
            });
            setFeeConfig(config);
        };

        const unsubConfig = listenToPlatformConfig(handleConfigUpdate);
        const unsubCats = listenToCategories(setServiceCategories);
        return () => { unsubConfig(); unsubCats(); };
    }, []);

    useEffect(() => {
        if (feeConfig.googleMapsApiKey && !document.getElementById('google-maps-script')) {
            const script = document.createElement('script');
            script.id = 'google-maps-script';
            script.src = `https://maps.googleapis.com/maps/api/js?key=${feeConfig.googleMapsApiKey}&libraries=geometry,places,marker`;
            script.async = true; script.defer = true;
            document.head.appendChild(script);
        }
    }, [feeConfig.googleMapsApiKey]);

    useEffect(() => {
        if (isLoggedIn && userProfile.phone) {
            const unsub = listenToMyJobs(userProfile.phone, userProfile.id || '', role, (jobs) => {
                jobs.forEach(job => {
                    // Update status ref finally for next iteration
                    lastJobStatuses.current[job.id] = job.status;

                    // Special Check for Completion Requested State Change
                    const prevComp = lastJobStatuses.current[`${job.id}_comp`];
                    if (role === UserRole.CUSTOMER && job.completionRequested && !prevComp) {
                        notificationService.showNotification('Completion Requested!', { body: `Review proofs from ${job.workerName}.`, isJobNotification: true });
                        lastJobStatuses.current[`${job.id}_comp`] = 'true';
                    }
                });
                const jobsList = jobs || [];
                myJobsRef.current = jobsList;
                setMyJobs(jobsList);
                if (role === UserRole.WORKER) {
                    const pendingJob = jobs?.find(j => j.status === 'PENDING' && !j.isBroadcast);
                    if (pendingJob && activeWorkerProfile?.isOnline) {
                        setIncomingJob(prev => {
                            if (!prev || prev.id !== pendingJob.id) return pendingJob;
                            return prev;
                        });
                    }
                    else if (!pendingJob) {
                        setIncomingJob(prev => prev ? null : prev);
                    }
                }
            });
            return () => unsub();
        }
    }, [isLoggedIn, userProfile.phone, role, activeWorkerProfile?.id, activeWorkerProfile?.isOnline]);

    useEffect(() => {
        if (isLoggedIn && role === UserRole.WORKER && activeWorkerProfile?.isOnline) {
            const workerId = activeWorkerProfile.id;
            const workerPhone = activeWorkerProfile.phone;
            const workerSkill = activeWorkerProfile.skill;

            const unsubJobs = listenToBroadcastJobs(workerSkill, (jobs: JobRequest[]) => {
                // Use ref to check busy state (avoids re-subscribing on myJobs change)
                const currentJobs = myJobsRef.current;
                const isBusy = currentJobs.some(j => ['ACCEPTED', 'IN_PROGRESS', 'OTP_VERIFIED'].includes(j.status) && (j.workerId === workerId || j.workerPhone === workerPhone));
                if (isBusy) { setBroadcastJobs([]); return; }
                setBroadcastJobs(jobs || []);

                const matchingJob = jobs.find(j => j.status === 'OPEN' && j.skill === workerSkill && !alertedJobIdsRef.current.has(j.id));
                if (matchingJob) {
                    setIncomingJob(matchingJob);
                    if (!lastBroadcastIds.current.has(matchingJob.id)) {
                        // Notification is shown via Service Worker — works in background too
                        // Sound is delegated to device default (no external Audio fetch)
                        notificationService.showJobNotification({ jobId: matchingJob.id, title: 'New Broadcast Signal', customerName: 'Nearby Home', skill: matchingJob.skill, isBroadcast: true });
                        lastBroadcastIds.current.add(matchingJob.id);
                    }
                    alertedJobIdsRef.current.add(matchingJob.id);
                }
            });
            return () => unsubJobs();
        } else if (isLoggedIn && role === UserRole.WORKER && !activeWorkerProfile?.isOnline) {
            setBroadcastJobs([]);
        }
    }, [isLoggedIn, role, activeWorkerProfile?.isOnline, activeWorkerProfile?.skill, activeWorkerProfile?.id, activeWorkerProfile?.phone]);

    useEffect(() => {
        if (isLoggedIn && role === UserRole.WORKER && activeWorkerProfile?.id) {
            notificationService.syncWorkerToken(activeWorkerProfile.id);
            const channel = supabase.channel(`w_sync_${activeWorkerProfile.id}`)
                .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'workers', filter: `id=eq.${activeWorkerProfile.id}` }, (payload) => {
                    // ⚡ INSTANT UPDATE: Apply all fields directly from payload (no re-fetch needed)
                    if (payload.new) {
                        const d = payload.new;
                        setActiveWorkerProfile(prev => prev ? ({
                            ...prev,
                            name: d.name ?? prev.name,
                            isOnline: d.is_online ?? prev.isOnline,
                            verificationStatus: d.verification_status ?? prev.verificationStatus,
                            avatarUrl: d.avatar_url ?? prev.avatarUrl,
                            coverImageUrl: d.cover_image_url ?? prev.coverImageUrl,
                            hourlyRate: d.hourly_rate ?? prev.hourlyRate,
                            skill: d.skill ?? prev.skill,
                            bio: d.bio ?? prev.bio,
                            tags: d.tags ?? prev.tags,
                            totalEarned: d.total_earned ?? prev.totalEarned,
                            lifetimeJobsCompleted: d.lifetime_jobs_completed ?? prev.lifetimeJobsCompleted,
                            idProofUrl: d.id_proof_url ?? prev.idProofUrl,
                            upiId: d.upi_id ?? prev.upiId,
                            phone: d.phone ?? prev.phone,
                            status: d.status ?? prev.status,
                        }) : null);
                    }
                }).subscribe();
            return () => { supabase.removeChannel(channel); };
        }
    }, [isLoggedIn, role, activeWorkerProfile?.id]);

    useEffect(() => {
        if (!isLoggedIn || !userProfile.id || myJobs.length === 0) return;

        const activeJobIds = myJobs
            .filter(j => ['ACCEPTED', 'IN_PROGRESS', 'OTP_VERIFIED', 'PENDING'].includes(j.status))
            .map(j => j.id);

        if (activeJobIds.length === 0) return;

        // Listen for new messages across all active jobs
        const channel = supabase.channel(`global_messages_${userProfile.id}`)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'messages'
            }, (payload: any) => {
                const newMessage = payload.new;

                // Only notify if:
                // 1. Message belongs to one of the user's active jobs
                // 2. Sender is NOT the current user
                // 3. User is NOT currently chatting with that job
                if (activeJobIds.includes(newMessage.job_id) &&
                    newMessage.sender_id !== userProfile.id &&
                    chattingJob?.id !== newMessage.job_id) {

                    const job = myJobs.find(j => j.id === newMessage.job_id);
                    const senderName = role === UserRole.CUSTOMER ? (job?.workerName || 'Expert') : (job?.customerName || 'Customer');

                    notificationService.showMessageNotification({
                        senderId: newMessage.sender_id,
                        senderName: senderName,
                        message: newMessage.text
                    });
                }
            })
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }, [isLoggedIn, userProfile.id, myJobs, chattingJob, role]);

    useEffect(() => {
        let unsubWallet = () => { };
        if (userProfile.id) {
            unsubWallet = listenToWalletUpdates(userProfile.id, (bal: number, tx: any[]) => {
                setUserProfile(prev => ({ ...prev, walletBalance: bal, transactions: tx }));
                if (activeWorkerProfile) {
                    const earned = tx.filter(t => t.type === 'CREDIT').reduce((acc, t) => acc + t.amount, 0);
                    setActiveWorkerProfile(prev => prev ? ({ ...prev, totalEarned: earned }) : null);
                }
            });
        }
        return () => unsubWallet();
    }, [userProfile.id, activeWorkerProfile?.id]);

    // SELF-HEALING: Fetch Worker Profile if missing (immediate, no delay)
    useEffect(() => {
        if (isLoggedIn && role === UserRole.WORKER && !activeWorkerProfile && !isOnboarding && !isSessionLoading) {
            const retryFetch = async () => {
                const all = await getAllWorkers(true);
                const w = all.find(x => x.id === userProfile.id || x.phone === userProfile.phone || x.phone === localStorage.getItem('hh_phone'));
                if (w) setActiveWorkerProfile(w);
                else setIsOnboarding(true);
            };
            retryFetch(); // ⚡ No delay - fetch immediately
        }
    }, [isLoggedIn, role, activeWorkerProfile, isOnboarding, isSessionLoading, userProfile]);

    const handleAuthSuccess = async (identifier: string, forcedRole: UserRole, name?: string) => {
        // ✅ FIX: Wrap entire auth flow in a timeout to prevent infinite spinner in APK WebView
        const AUTH_TIMEOUT_MS = 15000; // 15 seconds max

        const authFlow = async (): Promise<boolean> => {
            console.log("🔐 handleAuthSuccess: Starting auth flow...");
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) { console.error("❌ No user from getUser()"); return false; }
            console.log("✅ Got user:", user.id);

            const lookup = user.email || user.phone || identifier;
            let existing = await getUserProfile(lookup);
            console.log("👤 Profile lookup result:", !!existing);

            if (!existing) {
                // Also try looking up by user.id directly (WebView may not have matching email/phone)
                existing = await getUserProfile(user.id);
                console.log("👤 Profile lookup by ID result:", !!existing);
            }

            if (!existing) {
                const skeleton: UserProfile = {
                    id: user.id, firstName: name ? name.split(' ')[0] : 'User', lastName: name ? name.split(' ').slice(1).join(' ') : '',
                    email: user.email || (identifier.includes('@') ? identifier : ''), phone: user.phone || (identifier.includes('@') ? '+91 ' : identifier),
                    gender: 'Male', dob: '', avatarUrl: 'https://cdn-icons-png.flaticon.com/512/149/149071.png', walletBalance: 0, language: 'en', status: 'active'
                };
                await saveUserProfile(skeleton);
                existing = skeleton;

                // FORCE ONBOARDING FOR NEW USERS
                console.log("🆕 New User Detected! Forcing Profile Setup.");
                setIsOnboarding(true);
                localStorage.setItem('hh_needs_onboarding', 'true');
            }
            if (existing && (existing.status === 'suspended' || existing.status === 'deleted')) {
                alert("Account suspended/deleted.");
                await supabase.auth.signOut();
                return false;
            }
            setRole(forcedRole);
            localStorage.setItem('hh_phone', lookup);
            localStorage.setItem('hh_role', forcedRole);
            setUserProfile(existing!);
            setIsLoggedIn(true);
            if (forcedRole === UserRole.WORKER) {
                let w = await getWorkerProfile(existing!.id || '');
                if (!w) {
                    const nw: WorkerProfile = {
                        id: existing!.id!, phone: existing!.phone || '', name: `${existing!.firstName} ${existing!.lastName}`,
                        skill: 'General', rating: 5.0, reviewCount: 0, distanceKm: 0, hourlyRate: 300, isOnline: false,
                        avatarUrl: existing!.avatarUrl || '', bio: '', verificationStatus: 'pending', coordinates: { x: 45, y: 45 },
                        lifetimeJobsCompleted: 0, totalEarned: 0, status: 'active', idProofUrl: '', tags: []
                    };
                    await saveWorkerProfile(nw);
                    w = nw;
                }
                if (w.status !== 'active') { alert("Expert account not active."); await supabase.auth.signOut(); return false; }

                // Strict check for completion
                const isWorkerComplete = w.skill !== 'General' && w.bio && w.bio !== 'Available for work';
                if (!isWorkerComplete) {
                    setIsOnboarding(true);
                    setActiveWorkerProfile(w);
                    return true;
                }
                setActiveWorkerProfile(w);
            }
            console.log("✅ handleAuthSuccess: Auth flow complete!");
            return true;
        };

        try {
            // Race the auth flow against a timeout
            const result = await Promise.race([
                authFlow(),
                new Promise<boolean>((_, reject) =>
                    setTimeout(() => reject(new Error("Login timed out. Please check your internet connection and try again.")), AUTH_TIMEOUT_MS)
                )
            ]);
            return result;
        } catch (e: any) {
            console.error("❌ handleAuthSuccess error:", e);
            return false;
        }
    };

    const handleOnboardingComplete = async (profile: UserProfile, workerDetails?: Partial<WorkerProfile>) => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const final = { ...userProfile, ...profile, id: user.id, status: 'active' as const };
            await saveUserProfile(final);
            setUserProfile(final);
            if (role === UserRole.WORKER) {
                const nw: WorkerProfile = {
                    id: user.id, phone: final.phone, name: `${final.firstName} ${final.lastName}`,
                    skill: workerDetails?.skill || 'General', rating: 5.0, reviewCount: 0, distanceKm: 0, hourlyRate: workerDetails?.hourlyRate || 300,
                    isOnline: false, avatarUrl: final.avatarUrl, bio: workerDetails?.bio || '', verificationStatus: 'pending',
                    coordinates: { x: 45, y: 45 }, lifetimeJobsCompleted: 0, totalEarned: 0, status: 'active',
                    idProofUrl: workerDetails?.idProofUrl || '', tags: []
                };
                await saveWorkerProfile(nw);
                setActiveWorkerProfile(nw);
            }
            setIsOnboarding(false);
            setShowTour(true);
        } catch (e) { console.error(e); }
    };

    const handleProfileUpdate = (p: UserProfile) => {
        setUserProfile(p);
        if (p.language) setLanguage(p.language);
        if (role === UserRole.WORKER && activeWorkerProfile) setActiveWorkerProfile(prev => prev ? ({ ...prev, name: `${p.firstName} ${p.lastName}`, avatarUrl: p.avatarUrl }) : null);
    };

    return (
        <SessionContext.Provider value={{
            showSplash, isSessionLoading,
            language, setLanguage,
            hasSelectedLang, setHasSelectedLang,
            role, setRole,
            isLoggedIn, setIsLoggedIn,
            isOnboarding, setIsOnboarding,
            showTour, setShowTour,
            serviceCategories, feeConfig,
            userProfile, setUserProfile,
            activeWorkerProfile, setActiveWorkerProfile,
            myJobs, broadcastJobs, incomingJob, setIncomingJob,
            performLogout, handleAuthSuccess, handleOnboardingComplete, handleProfileUpdate,
            setFeeConfig, chattingJob, setChattingJob, reloadSession
        }}>
            {children}
        </SessionContext.Provider>
    );
};

export const useSession = () => {
    const context = useContext(SessionContext);
    if (context === undefined) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
};
